import { useEffect, useMemo, useCallback, useState } from 'react';

// Utils
import { logger } from './utils/logger';
import { ENV_KEYS, TABS } from './constants/uiConstants';
import { isDemoMode, getSwitchUrl } from './utils/demoUtils';

// Auth0 imports
import Auth0ProviderWrapper from './auth/Auth0Provider';
import { useAuth } from './hooks/useAuth';
import { ToastProvider } from './hooks/useToast';

// Custom hooks
import { usePlayerData, useMatchData, useBreadData, useUIState } from './hooks';

// Components
import Navbar from './components/layout/Navbar';
import Footer from './components/layout/Footer';
import HeaderStats from './components/stats/HeaderStats';

// Tab components
import LeaderboardTab from './components/tabs/LeaderboardTab';
import BreadTrackerTab from './components/tabs/BreadTrackerTab';
import MatchesTab from './components/tabs/MatchesTab';
import AnalyticsTab from './components/tabs/AnalyticsTab';

// Error handling
import ErrorBoundary from './components/error/ErrorBoundary';
import TabErrorFallback from './components/error/TabErrorFallback';

// Modal components
import ProfileModal from './components/modals/ProfileModal';
import EditProfileModal from './components/modals/EditProfileModal';
import MatchRecordModal from './components/modals/MatchRecordModal';

import { calculatePlayerStats } from './utils/calculations/playerStats';
import { apiClient } from './services/api/secureApiClient';
import { chemistryNetwork } from './utils/chemistry/ChemistryNetwork';

// Environment detection
const useMockData = false; // Force API mode for debugging (Use env var REACT_APP_USE_MOCK_DATA if needed)
const isLocalStack = process.env[ENV_KEYS.USE_LOCALSTACK] === 'true';

// Performance tracking
const APP_START_TIME = performance.now();
logger.debug(`PERFORMANCE: React component loaded at ${APP_START_TIME}ms`);

function OfficeArenaInner() {
  const {
    user,
    isAuthenticated,
    isLoading: isAuthLoading,
    logout,
    loginWithRedirect,
    getAccessTokenSilently,
    canWriteMatches,
    assignedName,
    isAdmin,
  } = useAuth();

  // Set up API client authentication
  useEffect(() => {
    if (getAccessTokenSilently) {
      apiClient.setTokenGetter(getAccessTokenSilently);
    }
  }, [getAccessTokenSilently]);

  // Set up user context for LocalStack custom headers
  useEffect(() => {
    if (user) {
      // Enrich user context with assignedName from whitelist
      const enrichedUser = {
        ...user,
        name: assignedName || user.name || user.email?.split('@')[0]
      };
      apiClient.setUserContext(enrichedUser);
    }
  }, [user, assignedName]);

  // Initialize chemistry neural network with global weights
  useEffect(() => {
    const initChemistry = async () => {
      try {
        await chemistryNetwork.initialize(apiClient);
        logger.debug('Chemistry network initialized successfully');
      } catch (error) {
        logger.error('Failed to initialize chemistry network:', error);
      }
    };

    initChemistry();
  }, []); // Run once on mount

  // Custom hooks for state management
  const {
    players,
    userProfile,
    setPlayers,
    setUserProfile, // Needed for updating profile name locally
    loadPlayersData,
    handleQuickAddPlayer,
    handleCreateProfile,
  } = usePlayerData(user, assignedName);

  const {
    matches,
    formError,
    setFormError,
    loadMatchesData,
    handleSubmitMatch,
    handleVerifyMatch,
  } = useMatchData(userProfile, players, setPlayers);

  const {
    breadLogs,
    breadSortField,
    breadSortDirection,
    setBreadSortField,
    setBreadSortDirection,
    loadBreadLogsData,
    handleMarkPaid,
  } = useBreadData(userProfile, players, setPlayers);

  const {
    activeTab,
    setActiveTab,
    selectedPlayer,
    setSelectedPlayer,
    comparePlayer,
    setComparePlayer,
    showMatchModal,
    setShowMatchModal,
    showProfileModal,
    setShowProfileModal,
    setIsLoading,
  } = useUIState();

  // Edit Profile Modal state
  const [showEditProfileModal, setShowEditProfileModal] = useState(false);

  // Demo Mode Banner Logic
  const isDemo = isDemoMode();
  const switchUrl = getSwitchUrl();

  // Data loading operations
  const loadData = useCallback(async () => {
    const startTime = performance.now();

    // Check if profile setup is needed
    // Only prompt if user is whitelisted (canWriteMatches) AND has no profile yet AND has no assigned name
    // (If assignedName exists, usePlayerData handles creation/linking automatically)
    if (!userProfile && !isLocalStack && canWriteMatches && !assignedName) {
        if (isAuthenticated && !userProfile) {
            setShowProfileModal(true);
            setIsLoading(false);
            return;
        }
    }

    try {
      setIsLoading(true);

      // Load all data in parallel for better performance
      const loadStart = performance.now();
      const [playersResult] = await Promise.all([
        loadPlayersData(),
        loadMatchesData(),
        loadBreadLogsData()
      ]);
      logger.debug(`All data loaded in ${performance.now() - loadStart}ms`);

      // Check if profile setup is needed after loading players
      if (!playersResult?.userProfile && !isLocalStack && canWriteMatches && !assignedName) {
        setShowProfileModal(true);
        setIsLoading(false);
        return;
      }

      setIsLoading(false);
    } catch (error) {
      logger.error('Error loading data:', error);
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useMockData, isLocalStack, isAuthenticated, loadPlayersData, loadMatchesData, loadBreadLogsData, canWriteMatches, assignedName]);

  useEffect(() => {
    if (isAuthLoading) return;
    loadData();
  }, [isAuthenticated, isAuthLoading, loadData]);

  const playerStats = useMemo(() => {
    return calculatePlayerStats(players, matches);
  }, [players, matches]);

  // Enrich players with calculated match stats for accurate filtering
  const enrichedPlayers = useMemo(() => {
    return players.map(p => {
      const stats = playerStats[p.id];
      return {
        ...p,
        // Use calculated wins/losses/ratings from matches instead of stale database values
        wins: stats?.historicalWins || p.wins || 0,
        losses: stats?.historicalLosses || p.losses || 0,
        mu: stats?.mu || p.mu || 25.0,
        sigma: stats?.sigma || p.sigma || 8.333,
      };
    });
  }, [players, playerStats]);

  const handleCreateProfileWrapper = async e => {
    try {
      await handleCreateProfile(e);
      setShowProfileModal(false);
    } catch (error) {
      setFormError(error.message);
    }
  };

  const handleSaveProfile = async (newName) => {
    if (!userProfile?.id) throw new Error('No profile to update');
    try {
      // Lazy load service to avoid cyclic dependency issues if any
      const { updatePlayerProfile } = await import('./services/api/playerService');
      
      // Call service with correct signature: (id, name, setUserProfile, loadPlayersData)
      await updatePlayerProfile(
        userProfile.id, 
        newName, 
        setUserProfile, 
        loadPlayersData
      );
      
      setShowEditProfileModal(false);
    } catch (error) {
      logger.error('Error saving profile:', error);
      // setFormError?
    }
  };

  const handleSubmitMatchWrapper = async (e, matchesData) => {
    try {
      setFormError('');
      await handleSubmitMatch(e, matchesData);
      setShowMatchModal(false);
    } catch (error) {
      logger.error('Match submission failed:', error);
    }
  };

  return (
    <div className="min-h-screen font-sans bg-white text-zinc-900">
      {/* Demo Mode Banner */}
      {isDemo && (
        <div className="bg-indigo-600 text-white text-center py-2 text-xs font-bold tracking-wide uppercase sticky top-0 z-30">
          Demo Mode &nbsp; / &nbsp; Data resets on refresh &nbsp; / &nbsp; <a href={switchUrl} className="underline hover:text-indigo-200">Go to Live App</a>
        </div>
      )}

      <Navbar
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        user={user}
        userProfile={userProfile}
        isAuthenticated={isAuthenticated}
        isAuthLoading={isAuthLoading}
        logout={logout}
        loginWithRedirect={loginWithRedirect}
        setShowEditProfileModal={setShowEditProfileModal}
      />

      <main className="max-w-6xl mx-auto px-4 py-8">
        <HeaderStats
          players={enrichedPlayers}
          matches={matches}
          canWriteMatches={canWriteMatches}
          setShowMatchModal={setShowMatchModal}
          setFormError={setFormError}
        />

        {/* Modal Logic */}
        <ProfileModal
          show={showProfileModal}
          onSubmit={handleCreateProfileWrapper}
          onClose={() => {
            setShowProfileModal(false);
            setFormError('');
          }}
          formError={formError}
        />

        {/* Tabs */}
        {activeTab === TABS.LEADERBOARD && (
          <ErrorBoundary fallback={TabErrorFallback}>
            <LeaderboardTab
              players={enrichedPlayers}
              userProfile={userProfile}
              user={user}
              setSelectedPlayer={setSelectedPlayer}
              setActiveTab={setActiveTab}
              isAdmin={isAdmin}
            />
          </ErrorBoundary>
        )}

        {activeTab === TABS.BREAD && (
          <ErrorBoundary fallback={TabErrorFallback}>
            <BreadTrackerTab
              players={enrichedPlayers}
              userProfile={userProfile}
              playerStats={playerStats}
              breadLogs={breadLogs}
              handleMarkPaid={(playerId, type) =>
                handleMarkPaid(playerId, type, loadPlayersData)
              }
              breadSortField={breadSortField}
              breadSortDirection={breadSortDirection}
              setBreadSortField={setBreadSortField}
              setBreadSortDirection={setBreadSortDirection}
              setSelectedPlayer={setSelectedPlayer}
              setActiveTab={setActiveTab}
              isAdmin={isAdmin}
            />
          </ErrorBoundary>
        )}

        {activeTab === TABS.MATCHES && (
          <ErrorBoundary fallback={TabErrorFallback}>
            <MatchesTab
              matches={matches}
              players={enrichedPlayers}
              handleVerifyMatch={handleVerifyMatch}
              userProfile={userProfile}
              setSelectedPlayer={setSelectedPlayer}
              setActiveTab={setActiveTab}
            />
          </ErrorBoundary>
        )}

        {activeTab === TABS.ANALYTICS && (
          <ErrorBoundary fallback={TabErrorFallback}>
            <AnalyticsTab
              selectedPlayer={selectedPlayer}
              setSelectedPlayer={setSelectedPlayer}
              comparePlayer={comparePlayer}
              setComparePlayer={setComparePlayer}
              players={enrichedPlayers}
              playerStats={playerStats}
              matches={matches}
              userProfile={userProfile}
              handleMarkMarkPaid={(playerId, type) => handleMarkPaid(playerId, type, loadPlayersData)}
              handleMarkPaid={(playerId, type) => handleMarkPaid(playerId, type, loadPlayersData)}
              isAdmin={isAdmin}
            />
          </ErrorBoundary>
        )}


        <MatchRecordModal
          show={showMatchModal}
          onSubmit={handleSubmitMatchWrapper}
          onClose={() => setShowMatchModal(false)}
          formError={formError}
          players={enrichedPlayers}
          onQuickAddPlayer={handleQuickAddPlayer}
        />

        <EditProfileModal
          show={showEditProfileModal}
          onClose={() => setShowEditProfileModal(false)}
          userProfile={userProfile}
          user={user}
          onSave={handleSaveProfile}
          players={enrichedPlayers}
        />
      </main>

      <Footer />
    </div>
  );
}

function OfficeArena() {
  return (
    <Auth0ProviderWrapper>
      <ToastProvider>
        <OfficeArenaInner />
      </ToastProvider>
    </Auth0ProviderWrapper>
  );
}

export default OfficeArena;